import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'CRITICAL';
  category: string;
  event_type: string;
  message: string;
  user_id?: string;
  project_id?: string;
  container_id?: string;
  metadata?: Record<string, any>;
  error_stack?: string;
  request_id?: string;
  ip_address?: string;
  user_agent?: string;
  endpoint?: string;
  http_method?: string;
  http_status?: number;
  response_time_ms?: number;
}

interface LogStats {
  levelCounts: Record<string, number>;
  categoryCounts: Record<string, number>;
  recentErrors: LogEntry[];
  topUsers: Array<{ userId: string; count: number }>;
  totalLogs: number;
}

interface Filters {
  level: string[];
  category: string[];
  eventType: string;
  userId: string;
  projectId: string;
  searchText: string;
  startDate: string;
  endDate: string;
}

const LOG_LEVELS = ['DEBUG', 'INFO', 'WARN', 'ERROR', 'CRITICAL'];
const LOG_CATEGORIES = ['auth', 'project', 'devserver', 'database', 'container', 'file', 'api', 'security', 'system'];

const getLevelColor = (level: string) => {
  switch (level) {
    case 'DEBUG': return 'text-gray-400';
    case 'INFO': return 'text-blue-400';
    case 'WARN': return 'text-yellow-400';
    case 'ERROR': return 'text-red-400';
    case 'CRITICAL': return 'text-red-600 font-bold';
    default: return 'text-gray-300';
  }
};

const getLevelBadgeColor = (level: string) => {
  switch (level) {
    case 'DEBUG': return 'bg-gray-600 text-white';
    case 'INFO': return 'bg-blue-600 text-white';
    case 'WARN': return 'bg-yellow-600 text-white';
    case 'ERROR': return 'bg-red-600 text-white';
    case 'CRITICAL': return 'bg-red-800 text-white';
    default: return 'bg-gray-500 text-white';
  }
};

export default function LogsViewer() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [stats, setStats] = useState<LogStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [relatedLogs, setRelatedLogs] = useState<LogEntry[]>([]);

  // Filters
  const [filters, setFilters] = useState<Filters>({
    level: [],
    category: [],
    eventType: '',
    userId: '',
    projectId: '',
    searchText: '',
    startDate: '',
    endDate: '',
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(5000);

  // View mode
  const [view, setView] = useState<'logs' | 'stats'>('logs');

  useEffect(() => {
    fetchLogs();
  }, [filters, page]);

  useEffect(() => {
    if (view === 'stats') {
      fetchStats();
    }
  }, [view, filters.startDate, filters.endDate]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchLogs();
      }, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, filters, page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Not authenticated');
        return;
      }

      const baseURL = import.meta.env.VITE_API_URL || '/api';
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (filters.level.length > 0) params.append('level', filters.level.join(','));
      if (filters.category.length > 0) params.append('category', filters.category.join(','));
      if (filters.eventType) params.append('eventType', filters.eventType);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.projectId) params.append('projectId', filters.projectId);
      if (filters.searchText) params.append('searchText', filters.searchText);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await fetch(`${baseURL}/admin/logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch logs');

      const data = await response.json();
      setLogs(data.logs);
      setTotalPages(data.pagination.totalPages);
      setTotal(data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const baseURL = import.meta.env.VITE_API_URL || '/api';
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await fetch(`${baseURL}/admin/logs/stats?${params}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to fetch statistics');
    }
  };

  const fetchLogDetail = async (logId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const baseURL = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${baseURL}/admin/logs/${logId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch log detail');

      const data = await response.json();
      setRelatedLogs(data.relatedLogs);
    } catch (error) {
      console.error('Failed to fetch log detail:', error);
    }
  };

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const baseURL = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${baseURL}/admin/logs/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...filters,
          level: filters.level.length > 0 ? filters.level : undefined,
          category: filters.category.length > 0 ? filters.category : undefined,
          format,
        }),
      });

      if (!response.ok) throw new Error('Failed to export logs');

      if (format === 'csv') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs-${Date.now()}.csv`;
        a.click();
      } else {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data.logs, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs-${Date.now()}.json`;
        a.click();
      }

      toast.success(`Exported ${format.toUpperCase()} successfully`);
    } catch (error) {
      console.error('Failed to export logs:', error);
      toast.error('Failed to export logs');
    }
  };

  const handleCleanup = async () => {
    if (!confirm('Are you sure you want to delete logs older than 30 days?')) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const baseURL = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${baseURL}/admin/logs/cleanup`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ days: 30 }),
      });

      if (!response.ok) throw new Error('Failed to cleanup logs');

      toast.success('Logs cleaned up successfully');
      fetchLogs();
    } catch (error) {
      console.error('Failed to cleanup logs:', error);
      toast.error('Failed to cleanup logs');
    }
  };

  const toggleLevel = (level: string) => {
    setFilters(prev => ({
      ...prev,
      level: prev.level.includes(level)
        ? prev.level.filter(l => l !== level)
        : [...prev.level, level],
    }));
    setPage(1);
  };

  const toggleCategory = (category: string) => {
    setFilters(prev => ({
      ...prev,
      category: prev.category.includes(category)
        ? prev.category.filter(c => c !== category)
        : [...prev.category, category],
    }));
    setPage(1);
  };

  const handleLogClick = (log: LogEntry) => {
    if (expandedLog === log.id) {
      setExpandedLog(null);
      setRelatedLogs([]);
    } else {
      setExpandedLog(log.id);
      if (log.request_id) {
        fetchLogDetail(log.id);
      }
    }
  };

  const filterByRequestId = (requestId: string) => {
    setFilters(prev => ({ ...prev, searchText: requestId }));
    setPage(1);
    setExpandedLog(null);
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">System Logs</h2>
          <div className="flex items-center gap-2">
            <Button
              variant={view === 'logs' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setView('logs')}
            >
              Logs
            </Button>
            <Button
              variant={view === 'stats' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setView('stats')}
            >
              Statistics
            </Button>
          </div>
        </div>

        {view === 'logs' && (
          <>
            {/* Filters */}
            <div className="space-y-3">
              {/* Level Filter */}
              <div>
                <label className="text-sm font-medium mb-1 block">Level</label>
                <div className="flex flex-wrap gap-2">
                  {LOG_LEVELS.map(level => (
                    <button
                      key={level}
                      onClick={() => toggleLevel(level)}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                        filters.level.includes(level)
                          ? getLevelBadgeColor(level)
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category Filter */}
              <div>
                <label className="text-sm font-medium mb-1 block">Category</label>
                <div className="flex flex-wrap gap-2">
                  {LOG_CATEGORIES.map(category => (
                    <button
                      key={category}
                      onClick={() => toggleCategory(category)}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                        filters.category.includes(category)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Search Text</label>
                  <input
                    type="text"
                    value={filters.searchText}
                    onChange={(e) => {
                      setFilters(prev => ({ ...prev, searchText: e.target.value }));
                      setPage(1);
                    }}
                    placeholder="Search message or event type..."
                    className="w-full px-3 py-1.5 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Event Type</label>
                  <input
                    type="text"
                    value={filters.eventType}
                    onChange={(e) => {
                      setFilters(prev => ({ ...prev, eventType: e.target.value }));
                      setPage(1);
                    }}
                    placeholder="e.g., api.get.200"
                    className="w-full px-3 py-1.5 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">User ID</label>
                  <input
                    type="text"
                    value={filters.userId}
                    onChange={(e) => {
                      setFilters(prev => ({ ...prev, userId: e.target.value }));
                      setPage(1);
                    }}
                    placeholder="User UUID"
                    className="w-full px-3 py-1.5 border rounded text-sm"
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Start Date</label>
                  <input
                    type="datetime-local"
                    value={filters.startDate}
                    onChange={(e) => {
                      setFilters(prev => ({ ...prev, startDate: e.target.value }));
                      setPage(1);
                    }}
                    className="w-full px-3 py-1.5 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">End Date</label>
                  <input
                    type="datetime-local"
                    value={filters.endDate}
                    onChange={(e) => {
                      setFilters(prev => ({ ...prev, endDate: e.target.value }));
                      setPage(1);
                    }}
                    className="w-full px-3 py-1.5 border rounded text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="cursor-pointer"
                  />
                  Auto-refresh
                </label>
                {autoRefresh && (
                  <select
                    value={refreshInterval}
                    onChange={(e) => setRefreshInterval(Number(e.target.value))}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    <option value={1000}>1s</option>
                    <option value={5000}>5s</option>
                    <option value={10000}>10s</option>
                    <option value={30000}>30s</option>
                  </select>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleExport('csv')}>
                  Export CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleExport('json')}>
                  Export JSON
                </Button>
                <Button variant="destructive" size="sm" onClick={handleCleanup}>
                  Cleanup Old Logs
                </Button>
                <Button variant="default" size="sm" onClick={fetchLogs} disabled={loading}>
                  {loading ? 'Loading...' : 'Refresh'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {view === 'logs' ? (
          <>
            {/* Pagination Info */}
            <div className="mb-3 text-sm text-gray-600">
              Showing {logs.length} of {total} logs (Page {page} of {totalPages})
            </div>

            {/* Logs Table */}
            {logs.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                No logs found matching your filters
              </div>
            ) : (
              <div className="space-y-2">
                {logs.map((log) => (
                  <div key={log.id} className="border rounded-lg overflow-hidden">
                    <div
                      onClick={() => handleLogClick(log)}
                      className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getLevelBadgeColor(log.level)}`}>
                          {log.level}
                        </span>
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                          {log.category}
                        </span>
                        <span className="text-xs text-gray-500 font-mono">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                        <span className="flex-1 text-sm">{log.message}</span>
                        {log.response_time_ms && (
                          <span className="text-xs text-gray-500">
                            {log.response_time_ms}ms
                          </span>
                        )}
                      </div>
                      <div className="mt-1 ml-24 text-xs text-gray-600">
                        {log.event_type}
                        {log.endpoint && ` • ${log.http_method} ${log.endpoint}`}
                        {log.http_status && ` • ${log.http_status}`}
                      </div>
                    </div>

                    {expandedLog === log.id && (
                      <div className="bg-gray-50 p-4 border-t space-y-3">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {log.user_id && (
                            <div>
                              <span className="font-medium">User ID:</span> {log.user_id}
                            </div>
                          )}
                          {log.project_id && (
                            <div>
                              <span className="font-medium">Project ID:</span> {log.project_id}
                            </div>
                          )}
                          {log.container_id && (
                            <div>
                              <span className="font-medium">Container ID:</span> {log.container_id}
                            </div>
                          )}
                          {log.request_id && (
                            <div>
                              <span className="font-medium">Request ID:</span>{' '}
                              <button
                                onClick={() => filterByRequestId(log.request_id!)}
                                className="text-blue-600 hover:underline"
                              >
                                {log.request_id}
                              </button>
                            </div>
                          )}
                          {log.ip_address && (
                            <div>
                              <span className="font-medium">IP Address:</span> {log.ip_address}
                            </div>
                          )}
                        </div>

                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                          <div>
                            <div className="font-medium text-sm mb-1">Metadata:</div>
                            <pre className="bg-black text-green-400 p-2 rounded text-xs overflow-auto max-h-40">
                              {JSON.stringify(log.metadata, null, 2)}
                            </pre>
                          </div>
                        )}

                        {log.error_stack && (
                          <div>
                            <div className="font-medium text-sm mb-1 text-red-600">Error Stack:</div>
                            <pre className="bg-black text-red-400 p-2 rounded text-xs overflow-auto max-h-60">
                              {log.error_stack}
                            </pre>
                          </div>
                        )}

                        {log.user_agent && (
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">User Agent:</span> {log.user_agent}
                          </div>
                        )}

                        {relatedLogs.length > 0 && (
                          <div>
                            <div className="font-medium text-sm mb-2">Related Logs ({relatedLogs.length}):</div>
                            <div className="space-y-1 max-h-60 overflow-auto">
                              {relatedLogs.map((related) => (
                                <div key={related.id} className="text-xs p-2 bg-white rounded border">
                                  <span className={`font-medium ${getLevelColor(related.level)}`}>
                                    [{related.level}]
                                  </span>{' '}
                                  <span className="text-gray-500">
                                    {new Date(related.timestamp).toLocaleTimeString()}
                                  </span>{' '}
                                  {related.message}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        ) : (
          // Statistics View
          stats && (
            <div className="space-y-6">
              {/* Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border rounded-lg p-4">
                  <div className="text-2xl font-bold">{stats.totalLogs.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Total Logs</div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="text-2xl font-bold text-red-600">
                    {(stats.levelCounts.ERROR || 0) + (stats.levelCounts.CRITICAL || 0)}
                  </div>
                  <div className="text-sm text-gray-600">Errors</div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="text-2xl font-bold text-yellow-600">
                    {stats.levelCounts.WARN || 0}
                  </div>
                  <div className="text-sm text-gray-600">Warnings</div>
                </div>
              </div>

              {/* Level Distribution */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Logs by Level</h3>
                <div className="space-y-2">
                  {Object.entries(stats.levelCounts).map(([level, count]) => (
                    <div key={level} className="flex items-center gap-3">
                      <span className={`w-20 text-sm font-medium ${getLevelColor(level)}`}>
                        {level}
                      </span>
                      <div className="flex-1 bg-gray-200 rounded-full h-6">
                        <div
                          className={`h-6 rounded-full flex items-center justify-end px-2 text-white text-xs ${
                            level === 'ERROR' || level === 'CRITICAL' ? 'bg-red-600' :
                            level === 'WARN' ? 'bg-yellow-600' : 'bg-blue-600'
                          }`}
                          style={{ width: `${(count / stats.totalLogs) * 100}%` }}
                        >
                          {count}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category Distribution */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Logs by Category</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(stats.categoryCounts).map(([category, count]) => (
                    <div key={category} className="border rounded p-3">
                      <div className="text-lg font-bold">{count}</div>
                      <div className="text-sm text-gray-600">{category}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Errors */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Recent Errors</h3>
                <div className="space-y-2">
                  {stats.recentErrors.map((error) => (
                    <div key={error.id} className="border-l-4 border-red-600 pl-3 py-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className={`text-sm font-medium ${getLevelColor(error.level)}`}>
                            [{error.level}] {error.message}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {error.event_type} • {new Date(error.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Users */}
              {stats.topUsers.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3">Most Active Users</h3>
                  <div className="space-y-2">
                    {stats.topUsers.map((user, idx) => (
                      <div key={user.userId} className="flex items-center gap-3">
                        <span className="text-gray-500 w-6">#{idx + 1}</span>
                        <span className="flex-1 font-mono text-sm">{user.userId}</span>
                        <span className="font-semibold">{user.count} logs</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}
